// Shared weigh-in store for the Weights tab and the Prep tab.
//
// Both pages persist to the same server-side `weight_entries` rows (one row per
// editable cell, keyed by section + entry_key), so numbers entered on one page
// show up on the other and two people entering at once never clobber each other.
//
// This hook exists mainly to make saving VISIBLE. The original inline
// implementation swallowed save failures:
//
//     .catch(() => { /* keep local; retries on next edit */ })
//
// On kitchen wifi a save could fail with zero indication on screen, and unlike a
// manager reviewing the sheet, a prep cook has no way to notice the number never
// landed. Every cell now reports pending / saved / failed, failed cells are
// retried automatically, and the caller can surface a count of what's still out.

import { useState, useEffect, useRef, useCallback } from 'react'
import { weightsAPI } from './api'

const SAVE_DEBOUNCE_MS = 400
const SYNC_INTERVAL_MS = 7000
const EDIT_PAUSE_MS = 5000     // don't merge server state while someone is typing
const RETRY_INTERVAL_MS = 5000

const EMPTY = () => ({
  onHand: {}, unit: {}, piece: {}, cut: {}, taskStatus: {}, taskOwner: {}, taskNote: {}, taskTarget: {},
  chefStatus: {}, chefOwner: {}, chefNote: {}, chefTarget: {},
  rawContainers: {}, cookedContainers: {},
})

// Cell identity for save-state bookkeeping. Scoped to the target menu so a
// failed cell on one menu can't show as failed on another menu's same-named row.
// Never parsed back apart — the key half embeds "::".
const cellId = (city, menu, section, key) => `${city}/${menu}|${section}|${key}`

function normalize(server) {
  const base = EMPTY()
  if (!server || typeof server !== 'object') return base
  for (const section of Object.keys(base)) {
    if (server[section] && typeof server[section] === 'object') {
      base[section] = { ...server[section] }
    }
  }
  return base
}

// Fire one cell at the server. Value semantics match the router: empty/null clears.
function put(entry) {
  const { city, menu, section, key, value } = entry
  return weightsAPI.set(city, menu, section, String(key), value === '' || value == null ? null : String(value))
}

export function useWeightsStore(cityId, menuId) {
  const [inputs, setInputs] = useState(EMPTY)
  const [saveState, setSaveState] = useState({})  // cellId -> 'pending' | 'saved' | 'failed'
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const retryLoad = useCallback(() => setLoadAttempt((n) => n + 1), [])

  const saveTimers = useRef({})       // cellId -> debounce timer
  const outboxRef = useRef({})        // cellId -> { city, menu, section, key, value, failed }
  const lastEditRef = useRef(0)       // pauses live-sync while typing
  const syncMarkerRef = useRef(null)  // last-seen server change marker (cheap polling)
  const targetRef = useRef({ cityId, menuId })

  targetRef.current = { cityId, menuId }

  // Merge server state, preserving any cell whose local edit hasn't been
  // confirmed yet — otherwise a poll (or a reload) silently reverts a value the
  // cook can see on screen but that never reached the server.
  const mergeServer = useCallback((server) => {
    const { cityId: city, menuId: menu } = targetRef.current
    setInputs(() => {
      const next = normalize(server)
      for (const entry of Object.values(outboxRef.current)) {
        if (entry.city !== city || entry.menu !== menu) continue
        next[entry.section] = { ...next[entry.section], [entry.key]: entry.value }
      }
      return next
    })
  }, [])

  // Send one cell. Kept out of the debounce so the retry loop can reuse it. Each
  // entry carries its own city/menu, so a queued save always lands on the menu it
  // was typed against even if the user has since switched menus.
  const send = useCallback((id) => {
    const entry = outboxRef.current[id]
    if (!entry) return
    entry.failed = false
    setSaveState((s) => (s[id] === 'pending' ? s : { ...s, [id]: 'pending' }))
    put(entry)
      .then(() => {
        // A newer edit may have landed in the outbox while this was in flight —
        // leave it there so the newer value still gets sent.
        if (outboxRef.current[id]?.value !== entry.value) return
        delete outboxRef.current[id]
        setSaveState((s) => ({ ...s, [id]: 'saved' }))
        syncMarkerRef.current = null  // re-sync on the next poll
      })
      .catch(() => {
        // Stays in the outbox, flagged for the retry loop. The flag lives on the
        // entry rather than in saveState so the retry loop never has to read
        // render state to decide what to re-send.
        if (outboxRef.current[id]) outboxRef.current[id].failed = true
        setSaveState((s) => ({ ...s, [id]: 'failed' }))
      })
  }, [])

  // Load everything already entered for this menu.
  useEffect(() => {
    if (!cityId || !menuId) {
      setInputs(EMPTY())
      setLoading(false)
      return
    }
    let cancelled = false
    syncMarkerRef.current = null
    setLoading(true)
    setInputs(EMPTY())
    setLoadError(false)
    weightsAPI
      .get(cityId, menuId)
      .then((saved) => { if (!cancelled) mergeServer(saved) })
      .catch(() => { if (!cancelled) setLoadError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [cityId, menuId, mergeServer, loadAttempt])

  // Live sync: pick up entries made by anyone else on this menu.
  useEffect(() => {
    if (!cityId || !menuId) return
    let stopped = false
    async function tick() {
      if (stopped || Date.now() - lastEditRef.current < EDIT_PAUSE_MS) return
      try {
        const lm = await weightsAPI.lastModified(cityId, menuId)
        const marker = `${lm.last_modified}|${lm.count}`
        if (marker === syncMarkerRef.current) return
        const server = await weightsAPI.get(cityId, menuId)
        if (stopped || Date.now() - lastEditRef.current < EDIT_PAUSE_MS) return
        syncMarkerRef.current = marker
        mergeServer(server)
        setLoadError(false)
      } catch { /* offline — retry next tick */ }
    }
    const id = setInterval(tick, SYNC_INTERVAL_MS)
    return () => { stopped = true; clearInterval(id) }
  }, [cityId, menuId, mergeServer])

  // Retry loop for failed saves — plus an immediate flush when the device comes
  // back online, which on kitchen wifi is the common case.
  useEffect(() => {
    function flushFailed() {
      for (const [id, entry] of Object.entries(outboxRef.current)) {
        if (entry.failed) send(id)
      }
    }
    const timer = setInterval(flushFailed, RETRY_INTERVAL_MS)
    window.addEventListener('online', flushFailed)
    return () => {
      clearInterval(timer)
      window.removeEventListener('online', flushFailed)
    }
  }, [send])

  // Update one cell locally, then debounce-save it to the shared store.
  const setCell = useCallback((section, key, value) => {
    lastEditRef.current = Date.now()
    setInputs((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }))
    const { cityId: city, menuId: menu } = targetRef.current
    if (!city || !menu) return
    const id = cellId(city, menu, section, key)
    outboxRef.current[id] = { city, menu, section, key, value, failed: false }
    setSaveState((s) => ({ ...s, [id]: 'pending' }))
    clearTimeout(saveTimers.current[id])
    saveTimers.current[id] = setTimeout(() => send(id), SAVE_DEBOUNCE_MS)
  }, [send])

  const clearAll = useCallback(async () => {
    if (Object.keys(outboxRef.current).length) {
      window.alert('Wait for pending saves, or retry failed saves, before resetting this menu.')
      return
    }
    if (!window.confirm('Reset ALL kitchen progress for this menu? This clears prep and chef weights, raw/cooked container counts, task status, assignments, notes, side completion, and plating checkoffs for everyone. The meal order is kept. This cannot be undone.')) return
    const { cityId: city, menuId: menu } = targetRef.current
    if (!city || !menu) return
    try {
      await weightsAPI.clear(city, menu)
      if (targetRef.current.cityId === city && targetRef.current.menuId === menu) {
        setInputs(EMPTY())
        setSaveState({})
        syncMarkerRef.current = null
      }
    } catch {
      window.alert('Reset failed. Your saved kitchen progress has been kept. Please try again.')
    }
  }, [])

  // What to show next to a cell: an in-flight or failed save wins; otherwise a
  // cell that holds a value is one the server has (it either came from there or
  // was confirmed on the way in).
  const stateFor = useCallback((section, key) => {
    const s = saveState[cellId(cityId, menuId, section, key)]
    if (s === 'pending' || s === 'failed') return s
    const v = inputs[section]?.[key]
    return v === undefined || v === null || v === '' ? 'none' : 'saved'
  }, [saveState, inputs, cityId, menuId])

  const retryFailed = useCallback(() => {
    for (const [id, entry] of Object.entries(outboxRef.current)) {
      if (entry.failed) send(id)
    }
  }, [send])

  // On unmount (tab switch mid-entry), send anything still sitting in the outbox
  // instead of dropping it with its debounce timer. Fire-and-forget: the fetch
  // outlives the component, and there's no state left to update.
  useEffect(() => {
    const timers = saveTimers.current
    const outbox = outboxRef.current
    return () => {
      Object.values(timers).forEach(clearTimeout)
      Object.values(outbox).forEach((entry) => { put(entry).catch(() => {}) })
    }
  }, [])

  return {
    inputs,
    loading,
    loadError,
    retryLoad,
    setCell,
    clearAll,
    stateFor,
    retryFailed,
    failedCount: Object.values(saveState).filter((v) => v === 'failed').length,
  }
}
